/**
 * ACTUAL metres run per casing string, per ticket (api#830, migration 0424).
 *
 * A "pipe lot" in TNT's word is a casing string. The field records the metres
 * actually run on each string on the TICKET — a multi-day job's daily tickets
 * each carry that day's metres per string.
 *
 * ─── WIRE SHAPE (read off fieldiq-api src/lib/ticket-string-metres.ts) ──────
 *
 *   write  casingStringMetres: [{ casingStringId, metres }]
 *          `metres: null` removes that string's entry; strings not sent are
 *          left as they are. Accepted on the office PUT /tickets/:id, the field
 *          PUT /employee/my-tickets/:id, POST .../field-approve and sync push
 *          `update`. NOT on ticket create — a ticket records metres on its
 *          first save.
 *   read   casingStringMetres: [{ casingStringId, stringNumber, metres }],
 *          metres a JSON number, [] when none recorded.
 *
 * ─── THE TOTAL IS THE SERVER'S ──────────────────────────────────────────────
 *
 * When a ticket holds string rows, the API writes `actual_metres_run` as their
 * sum, in the same transaction. The screen shows that column; it never adds the
 * strings up itself — two answers to one question, and the one on the device
 * would be wrong for every unsaved edit and every other hand's save.
 *
 * ─── THE SENTINEL: `null` recorded vs `[]` recorded ─────────────────────────
 *
 * `readRecordedMetres` returns `null` when the ticket carries NO
 * `casingStringMetres` key — a row cached by a bundle or an API that predates
 * 0424 — and a (possibly empty) map when it does. They mean opposite things:
 *
 *   null   "I do not know what is recorded" — a blank input must send NOTHING
 *          for that string, or it would delete an entry the office made that
 *          this device has never seen
 *   {}     "nothing is recorded" — a blank input sends `metres: null`, which
 *          removes nothing and keeps the payload the full set
 *
 * `?? []` or `|| {}` anywhere on the read collapses the first into the second
 * and turns a stale cache into a delete. Never do that here.
 */
import type { JobCasingString } from '../types/casing-string';

export interface CasingStringMetresEntry {
  casingStringId: string;
  /** null removes this string's entry. */
  metres: number | null;
}

/** casingStringId → metres as recorded. */
export type RecordedMetres = Record<string, number>;

/** What the inputs hold, keyed by casingStringId. '' is blank. */
export type MetresDraft = Record<string, string>;

/**
 * The recorded metres on a ticket, or `null` when the ticket carries no
 * `casingStringMetres` key at all. See the sentinel note above.
 *
 * An entry with `metres: null` is skipped: that is the shape an offline save
 * leaves on the device's copy (the queued write's entries, merged over the
 * cached row), and it means "removed".
 */
export function readRecordedMetres(ticket: any): RecordedMetres | null {
  const raw = ticket?.casingStringMetres ?? ticket?.casing_string_metres;
  if (!Array.isArray(raw)) return null;
  const out: RecordedMetres = {};
  for (const e of raw) {
    const id = e?.casingStringId ?? e?.casing_string_id;
    const m = e?.metres;
    if (typeof id !== 'string' || m === null || m === undefined || m === '') continue;
    const n = Number(m);
    if (Number.isFinite(n)) out[id] = n;
  }
  return out;
}

/** Seed the inputs from what is recorded. Blank where nothing is. */
export function draftFromRecorded(recorded: RecordedMetres | null): MetresDraft {
  const out: MetresDraft = {};
  for (const [id, m] of Object.entries(recorded ?? {})) out[id] = String(m);
  return out;
}

/** Only strings with a row id can carry metres — a legacy synthesized string has none. */
export function stringsThatTakeMetres(strings: JobCasingString[]): (JobCasingString & { id: string })[] {
  return strings
    .filter((s): s is JobCasingString & { id: string } => typeof s.id === 'string' && s.id.length > 0)
    .sort((a, b) => Number(a.string_number) - Number(b.string_number));
}

export type MetresPayload =
  | { ok: true; entries: CasingStringMetresEntry[] }
  | { ok: false; error: string };

/**
 * The `casingStringMetres` to send, or a refusal naming the string.
 *
 * With the recorded set KNOWN, every string is sent — blank as `null` — so the
 * payload is the full set and the device's merged copy after an offline save
 * is complete rather than a subset that reads as "the others were cleared".
 * With it UNKNOWN (`null`), only strings the hand filled in are sent.
 *
 * An empty `entries` means "send no key". The caller omits it.
 */
export function buildCasingStringMetres(
  strings: JobCasingString[],
  recorded: RecordedMetres | null,
  draft: MetresDraft,
): MetresPayload {
  const entries: CasingStringMetresEntry[] = [];
  for (const s of stringsThatTakeMetres(strings)) {
    const raw = (draft[s.id] ?? '').trim().replace(/,/g, '');
    if (raw === '') {
      if (recorded !== null) entries.push({ casingStringId: s.id, metres: null });
      continue;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) {
      return { ok: false, error: `Metres run on string ${s.string_number} must be a number, 0 or more.` };
    }
    entries.push({ casingStringId: s.id, metres: Math.round(n * 100) / 100 });
  }
  // All blank over nothing recorded says nothing: send no key, rather than a
  // list of removals of rows that do not exist on every save of every ticket.
  const nothingRecorded = recorded === null || Object.keys(recorded).length === 0;
  if (nothingRecorded && entries.every((e) => e.metres === null)) return { ok: true, entries: [] };
  return { ok: true, entries };
}

/** True when the ticket holds string rows, so its total is the server's sum of them. */
export function totalIsStringSum(recorded: RecordedMetres | null): boolean {
  return recorded !== null && Object.keys(recorded).length > 0;
}
